export type EmptyObject = { [K in string | number]: never };
export type NonEmptyArray<T> = [T, ...T[]];

export const isNull = <T>(value: T | null): value is null => value === null;

export const isNotNull = <T>(value: T | null): value is T => value !== null;

export const isNotNullOrUndefined = <T>(value: T | undefined | null): value is T => !!value;

export const isNullOrUndefined = (value: unknown): value is null | undefined => value == null;

export const isObjectType = (value: unknown) => typeof value === 'object';
export const isFunction = (value: unknown): value is (...args: any[]) => any => typeof value === 'function';
export const isNonEmptyArray = <T>(value: T[]): value is NonEmptyArray<T> => value.length >= 1;
export const isEmptyArray = <T>(value: T[]): value is [] => value?.length === 0;

// In this case its fine to use object. In other places use Record<string, unknown>.
export const isObject = <T extends object>(value: unknown): value is T =>
  !isNullOrUndefined(value) && !Array.isArray(value) && isObjectType(value) && !(value instanceof Date);

export const isEmptyObject = (value: unknown): value is EmptyObject =>
  isObject(value) && Object.keys(value).length === 0 && value.constructor === Object;

export const canBeFocused = <TElement extends Element | RadioNodeList>(
  el?: TElement
): el is TElement & { focus: () => void } => {
  return isNotNullOrUndefined(el) && 'focus' in el;
};

export const isFile = (value: unknown): value is File => value instanceof File;
export const isFileArray = (value: unknown): value is File[] =>
  Array.isArray(value) && value.every(v => v instanceof File);
export const isHTMLImageElement = (value: unknown): value is HTMLImageElement => value instanceof HTMLImageElement;
